import type { ReactNode } from "react";
import type { LedgerFinding } from "../../api/codeReview";
import { headlineOf } from "../../lib/ledgerFormat";
import { fileLine, stackWidths, SEVERITIES } from "../../lib/bobReports";

const SEV_CLASS: Readonly<Record<string, string>> = { BLOCK: "br-chip-block", FLAG: "br-chip-flag", SUGGEST: "br-chip-suggest" };
const SEV_BG: Readonly<Record<string, string>> = { BLOCK: "br-bg-block", FLAG: "br-bg-flag", SUGGEST: "br-bg-suggest" };

/** The shape carries the severity as well as the colour: octagon, triangle, circle. */
function SevGlyph({ severity }: { severity: string }) {
  const common = { width: 11, height: 11, viewBox: "0 0 12 12", "aria-hidden": true as const, fill: "currentColor" };
  if (severity === "BLOCK") return <svg {...common}><path d="M3.5 0.5h5l3 3v5l-3 3h-5l-3-3v-5z" /></svg>;
  if (severity === "FLAG") return <svg {...common}><path d="M6 0.8 11.4 11H0.6z" /></svg>;
  return <svg {...common}><circle cx="6" cy="6" r="5" /></svg>;
}

export function SevChip({ severity, count, size }: { severity: string; count?: number; size?: "sm" }) {
  return (
    <span className={`br-chip ${SEV_CLASS[severity] ?? "br-chip-cat"}${size ? ` br-${size}` : ""}`}>
      <SevGlyph severity={severity} />
      {count === undefined ? severity : `${count} ${severity}`}
    </span>
  );
}

export function CatChip({ children, size }: { children: ReactNode; size?: "lg" }) {
  return <span className={`br-chip br-chip-cat${size ? ` br-${size}` : ""}`}>{children}</span>;
}

/** A stacked BLOCK/FLAG/SUGGEST bar. Decorative: the counts are always printed beside it. */
export function SevStack({ counts, total }: { counts: Record<string, number>; total: number }) {
  const w = stackWidths(counts, total);
  return (
    <div className="br-stack" aria-hidden="true">
      {SEVERITIES.map((s) => (counts[s] ?? 0) > 0 && <span key={s} className={SEV_BG[s]} style={{ width: w[s] }} />)}
    </div>
  );
}

export function sevBg(severity: string): string {
  return SEV_BG[severity] ?? "br-bg-ink";
}

export function Tile({ label, value, valueColor, note, children }: {
  label: string;
  value: ReactNode;
  valueColor?: string;
  note?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="br-tile">
      <span className="br-eyebrow">{label}</span>
      <b style={valueColor ? { color: valueColor } : undefined}>{value}</b>
      {children}
      {note !== undefined && <span className="br-tiny br-muted">{note}</span>}
    </div>
  );
}

/** A finding as a compact card that opens it in the console. */
export function FindingCard({ finding, href, onOpen, fixed, extra, meta }: {
  finding: LedgerFinding;
  href: string;
  onOpen: (id: number) => void;
  fixed?: boolean;
  extra?: ReactNode;
  meta?: string;
}) {
  return (
    <a
      className="br-item"
      href={href}
      data-finding={finding.id}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        onOpen(finding.id);
      }}
    >
      <span className="br-item-head">
        {fixed && <span className="br-chip br-chip-ok">Fixed</span>}
        <SevChip severity={finding.severity} />
        {finding.category && <CatChip>{finding.category}</CatChip>}
        <span className="br-spacer" />
        <span className="br-mono br-tiny br-muted">#{finding.id}{meta ? `, ${meta}` : ""}</span>
      </span>
      <span className={`br-item-title${fixed ? " br-struck" : ""}`}>{headlineOf(finding)}</span>
      {extra}
      <span className={`br-mono br-tiny ${fixed ? "br-muted" : "br-soft"}`}>{fileLine(finding)}</span>
    </a>
  );
}

export function Loading({ what }: { what: string }) {
  return <div className="br-state" role="status">Loading {what}…</div>;
}

export function Failed({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="br-state" role="alert">
      {message}
      {onRetry && <> <button type="button" className="br-linkbtn" onClick={onRetry}>Try again</button></>}
    </div>
  );
}
